/**
 * ClientsManager Component
 * Admin view for managing client companies (CRUD + portal login invites)
 *
 * ValidKernel Defensive Build Standard applied:
 * - All API responses validated (Array.isArray guards, normalized records)
 * - All rendered values pass through safeText() / explicit fallbacks
 * - Null-safe access throughout; no raw objects can reach JSX
 */

import React, { useState, useEffect, useCallback } from 'react';
import { api, clientApi } from '../services/api';

// ============================================================================
// Types
// ============================================================================

interface Client {
  id: number;
  name: string;
  client_code: string;
  contact_name: string;
  email: string;
  phone: string;
  address: string;
  billing_email: string;
  payment_terms: number;
  tax_exempt: boolean;
  notes: string;
  is_active: boolean;
  created_at: string;
}

interface ClientFormState {
  name: string;
  client_code: string;
  contact_name: string;
  email: string;
  phone: string;
  address: string;
  billing_email: string;
  payment_terms: string; // kept as string for the input; parsed on submit
  tax_exempt: boolean;
  notes: string;
  is_active: boolean;
}

const EMPTY_FORM: ClientFormState = {
  name: '',
  client_code: '',
  contact_name: '',
  email: '',
  phone: '',
  address: '',
  billing_email: '',
  payment_terms: '30',
  tax_exempt: false,
  notes: '',
  is_active: true,
};

const CODE_PATTERN = /^[A-Za-z]{3}$/;

// ============================================================================
// Defensive helpers
// ============================================================================

/** Never let a non-string reach JSX. */
const safeText = (val: any): string => {
  if (val === null || val === undefined) return '';
  if (typeof val === 'string') return val;
  if (typeof val === 'number' || typeof val === 'boolean') return String(val);
  if (typeof val === 'object') {
    return val.name || val.label || val.title || val.message || JSON.stringify(val);
  }
  return String(val);
};

/** Normalize an API record to a fully-populated Client (null-safe). */
const normalizeClient = (raw: any): Client => {
  const c = raw || {};
  return {
    id: typeof c.id === 'number' ? c.id : Number(c.id) || 0,
    name: safeText(c.name),
    client_code: safeText(c.client_code).toUpperCase(),
    contact_name: safeText(c.contact_name),
    email: safeText(c.email),
    phone: safeText(c.phone),
    address: safeText(c.address),
    billing_email: safeText(c.billing_email),
    payment_terms: Number.isFinite(Number(c.payment_terms)) && c.payment_terms !== null && c.payment_terms !== ''
      ? Number(c.payment_terms)
      : 30,
    tax_exempt: Boolean(c.tax_exempt),
    notes: safeText(c.notes),
    is_active: c.is_active === undefined || c.is_active === null ? true : Boolean(c.is_active),
    created_at: safeText(c.created_at),
  };
};

/**
 * Suggest a 3-letter client code from a company name:
 * first 3 consonants if available, otherwise first 3 letters. Uppercase.
 */
const suggestClientCode = (name: string): string => {
  const letters = safeText(name).toUpperCase().replace(/[^A-Z]/g, '');
  const consonants = letters.replace(/[AEIOU]/g, '');
  return (consonants.length >= 3 ? consonants : letters).slice(0, 3);
};

// ============================================================================
// Component
// ============================================================================

const ClientsManager: React.FC = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState('');
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [form, setForm] = useState<ClientFormState>(EMPTY_FORM);
  const [codeTouched, setCodeTouched] = useState(false);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  // Invite state (per-row busy flag)
  const [invitingId, setInvitingId] = useState<number | null>(null);

  const fetchClients = useCallback(async () => {
    setLoading(true);
    setListError('');
    try {
      const response = await clientApi.list();
      if (response.success && response.data) {
        const rawList = Array.isArray(response.data.clients) ? response.data.clients : [];
        setClients(rawList.map(normalizeClient));
      } else {
        console.error('Failed to fetch clients:', response.error);
        setListError(safeText(response.error) || 'Failed to load clients');
        setClients([]);
      }
    } catch (error) {
      console.error('Failed to fetch clients:', error);
      setListError('Failed to load clients');
      setClients([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  // --------------------------------------------------------------------------
  // Filtering + stats
  // --------------------------------------------------------------------------

  const filteredClients = clients.filter(client => {
    if (!showInactive && !client.is_active) return false;
    const term = search.trim().toLowerCase();
    if (term) {
      const haystack = [client.name, client.client_code, client.contact_name, client.email]
        .map(v => safeText(v).toLowerCase());
      if (!haystack.some(v => v.includes(term))) return false;
    }
    return true;
  });

  const stats = {
    total: clients.length,
    active: clients.filter(c => c.is_active).length,
    withEmail: clients.filter(c => safeText(c.email).trim() !== '').length,
  };

  // --------------------------------------------------------------------------
  // Modal open/close
  // --------------------------------------------------------------------------

  const openAddModal = () => {
    setEditingClient(null);
    setForm({ ...EMPTY_FORM });
    setCodeTouched(false);
    setFormError('');
    setModalOpen(true);
  };

  const openEditModal = (client: Client) => {
    const c = normalizeClient(client);
    setEditingClient(c);
    setForm({
      name: c.name,
      client_code: c.client_code,
      contact_name: c.contact_name,
      email: c.email,
      phone: c.phone,
      address: c.address,
      billing_email: c.billing_email,
      payment_terms: String(c.payment_terms),
      tax_exempt: c.tax_exempt,
      notes: c.notes,
      is_active: c.is_active,
    });
    setCodeTouched(true); // never overwrite an existing code from name edits
    setFormError('');
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;
    setModalOpen(false);
    setEditingClient(null);
    setFormError('');
  };

  // --------------------------------------------------------------------------
  // Form handlers
  // --------------------------------------------------------------------------

  const handleNameChange = (value: string) => {
    setForm(prev => ({
      ...prev,
      name: value,
      // Auto-suggest code while adding, until the user edits the code manually
      client_code: !codeTouched && !editingClient ? suggestClientCode(value) : prev.client_code,
    }));
  };

  const handleCodeChange = (value: string) => {
    const cleaned = safeText(value).toUpperCase().replace(/[^A-Z]/g, '').slice(0, 3);
    setCodeTouched(true);
    setForm(prev => ({ ...prev, client_code: cleaned }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving) return;
    setFormError('');

    const name = form.name.trim();
    const code = form.client_code.trim().toUpperCase();

    if (!name) {
      setFormError('Company name is required.');
      return;
    }
    if (!CODE_PATTERN.test(code)) {
      setFormError('Client code must be exactly 3 letters (A-Z).');
      return;
    }

    const parsedTerms = parseInt(form.payment_terms, 10);
    const payload: Record<string, any> = {
      name,
      client_code: code,
      contact_name: form.contact_name.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      address: form.address.trim(),
      billing_email: form.billing_email.trim(),
      payment_terms: Number.isFinite(parsedTerms) ? parsedTerms : 30,
      tax_exempt: Boolean(form.tax_exempt),
      notes: form.notes,
    };

    setSaving(true);
    try {
      const response = editingClient
        ? await clientApi.update(editingClient.id, { ...payload, is_active: Boolean(form.is_active) })
        : await clientApi.create(payload);

      if (response.success) {
        setModalOpen(false);
        setEditingClient(null);
        await fetchClients();
      } else {
        setFormError(safeText(response.error) || 'Save failed. Please try again.');
      }
    } catch (error) {
      console.error('Failed to save client:', error);
      setFormError('Save failed. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // --------------------------------------------------------------------------
  // Invite login
  // --------------------------------------------------------------------------

  const handleInvite = async (client: Client) => {
    const email = safeText(client.email).trim();
    if (!email || invitingId !== null) return;

    const confirmed = window.confirm(
      `Send a portal login invite to ${email} (${safeText(client.name)})?`
    );
    if (!confirmed) return;

    setInvitingId(client.id);
    try {
      const response = await api.post('/api/companies/current/invite', {
        email,
        role: 'client',
      });
      if (response.success) {
        const message = safeText(response.data?.message);
        window.alert(message || `Invite sent to ${email}.`);
      } else {
        window.alert(`Invite failed: ${safeText(response.error) || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Failed to send invite:', error);
      window.alert('Invite failed: network error.');
    } finally {
      setInvitingId(null);
    }
  };

  // --------------------------------------------------------------------------
  // Render
  // --------------------------------------------------------------------------

  const codeValid = CODE_PATTERN.test(form.client_code);

  return (
    <div className="clients-manager">
      <div className="manager-header">
        <h2>Client Management</h2>
        <button className="btn primary" onClick={openAddModal}>+ Add Client</button>
      </div>

      {/* Stats */}
      <div className="stats-row">
        <div className="stat-pill">
          <span className="stat-value">{stats.total}</span>
          <span className="stat-label">Total</span>
        </div>
        <div className="stat-pill active">
          <span className="stat-value">{stats.active}</span>
          <span className="stat-label">Active</span>
        </div>
        <div className="stat-pill email">
          <span className="stat-value">{stats.withEmail}</span>
          <span className="stat-label">With Login Email</span>
        </div>
      </div>

      {/* Filters */}
      <div className="filters">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search by name, code, contact, or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <label className="toggle-inactive">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
          />
          <span>Show inactive</span>
        </label>
      </div>

      {listError && <div className="list-error">{safeText(listError)}</div>}

      {/* Clients Table */}
      {loading ? (
        <div className="loading">Loading clients...</div>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Code</th>
              <th>Name</th>
              <th>Contact</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Terms</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredClients.length === 0 ? (
              <tr>
                <td colSpan={8} className="empty-row">
                  {clients.length === 0
                    ? 'No clients yet. Click "+ Add Client" to create one.'
                    : 'No clients match the current filters.'}
                </td>
              </tr>
            ) : (
              filteredClients.map(client => (
                <tr key={client.id} className={client.is_active ? '' : 'inactive-row'}>
                  <td><span className="client-code">{safeText(client.client_code)}</span></td>
                  <td><span className="client-name">{safeText(client.name)}</span></td>
                  <td>{safeText(client.contact_name) || '—'}</td>
                  <td>{safeText(client.email) || '—'}</td>
                  <td>{safeText(client.phone) || '—'}</td>
                  <td>Net {safeText(client.payment_terms)}</td>
                  <td>
                    <span className={`status-badge ${client.is_active ? 'status-active' : 'status-inactive'}`}>
                      {client.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn small"
                        onClick={() => openEditModal(client)}
                      >
                        Edit
                      </button>
                      {safeText(client.email).trim() !== '' && (
                        <button
                          className="btn small"
                          disabled={invitingId !== null}
                          title="Email a portal login invite to this client"
                          onClick={() => handleInvite(client)}
                        >
                          {invitingId === client.id ? 'Inviting...' : 'Invite Login'}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      )}

      {/* Add / Edit Modal */}
      {modalOpen && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingClient ? 'Edit Client' : 'Add Client'}</h3>
              <button className="close-btn" onClick={closeModal} aria-label="Close">×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-content">
                {formError && <div className="form-error">{safeText(formError)}</div>}

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="client-name">Company Name *</label>
                    <input
                      id="client-name"
                      type="text"
                      value={form.name}
                      onChange={(e) => handleNameChange(e.target.value)}
                      required
                      autoFocus
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="client-code">Client Code *</label>
                    <input
                      id="client-code"
                      type="text"
                      value={form.client_code}
                      onChange={(e) => handleCodeChange(e.target.value)}
                      maxLength={3}
                      required
                      style={!codeValid && form.client_code.length > 0 ? { borderColor: 'var(--danger, #ef4444)' } : undefined}
                    />
                    <div className="form-hint">Exactly 3 letters (A-Z). Auto-suggested from the company name.</div>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="client-contact">Contact Name</label>
                    <input
                      id="client-contact"
                      type="text"
                      value={form.contact_name}
                      onChange={(e) => setForm(prev => ({ ...prev, contact_name: e.target.value }))}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="client-email">Email</label>
                    <input
                      id="client-email"
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm(prev => ({ ...prev, email: e.target.value }))}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="client-phone">Phone</label>
                    <input
                      id="client-phone"
                      type="tel"
                      value={form.phone}
                      onChange={(e) => setForm(prev => ({ ...prev, phone: e.target.value }))}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="client-billing-email">Billing Email</label>
                    <input
                      id="client-billing-email"
                      type="email"
                      value={form.billing_email}
                      onChange={(e) => setForm(prev => ({ ...prev, billing_email: e.target.value }))}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="client-address">Address</label>
                  <input
                    id="client-address"
                    type="text"
                    value={form.address}
                    onChange={(e) => setForm(prev => ({ ...prev, address: e.target.value }))}
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="client-terms">Payment Terms (days)</label>
                    <input
                      id="client-terms"
                      type="number"
                      min={0}
                      step={1}
                      value={form.payment_terms}
                      onChange={(e) => setForm(prev => ({ ...prev, payment_terms: e.target.value }))}
                    />
                  </div>
                  <div className="form-group checkbox">
                    <label>
                      <input
                        type="checkbox"
                        checked={form.tax_exempt}
                        onChange={(e) => setForm(prev => ({ ...prev, tax_exempt: e.target.checked }))}
                      />
                      <span>Tax Exempt</span>
                    </label>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="client-notes">Notes</label>
                  <textarea
                    id="client-notes"
                    rows={3}
                    value={form.notes}
                    onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))}
                  />
                </div>

                {editingClient && (
                  <div className="form-group checkbox">
                    <label>
                      <input
                        type="checkbox"
                        checked={form.is_active}
                        onChange={(e) => setForm(prev => ({ ...prev, is_active: e.target.checked }))}
                      />
                      <span>Active (uncheck to deactivate this client)</span>
                    </label>
                  </div>
                )}
              </div>

              <div className="modal-actions">
                <button type="button" className="btn secondary" onClick={closeModal} disabled={saving}>
                  Cancel
                </button>
                <button type="submit" className="btn primary" disabled={saving}>
                  {saving ? 'Saving...' : editingClient ? 'Save Changes' : 'Create Client'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        .clients-manager {
          padding: 0;
        }

        .manager-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
        }

        .manager-header h2 {
          margin: 0;
        }

        .stats-row {
          display: flex;
          gap: 12px;
          margin-bottom: 24px;
          flex-wrap: wrap;
        }

        .stat-pill {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 16px;
          background: var(--bg-tertiary, #334155);
          border-radius: 20px;
        }

        .stat-pill.active { background: rgba(34, 197, 94, 0.2); }
        .stat-pill.email { background: rgba(37, 99, 235, 0.2); }

        .stat-value {
          font-weight: 600;
        }

        .stat-label {
          font-size: 12px;
          color: var(--text-secondary);
        }

        .filters {
          display: flex;
          gap: 12px;
          margin-bottom: 16px;
          flex-wrap: wrap;
          align-items: center;
        }

        .search-box {
          flex: 1;
          min-width: 200px;
        }

        .search-box input {
          width: 100%;
          padding: 8px 16px;
          background: var(--bg-tertiary, #334155);
          border: 1px solid var(--border-color, #334155);
          border-radius: 8px;
          color: var(--text-primary);
        }

        .toggle-inactive {
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          color: var(--text-secondary);
          font-size: 14px;
          white-space: nowrap;
        }

        .list-error {
          padding: 12px 16px;
          margin-bottom: 16px;
          border-radius: 8px;
          background: rgba(239, 68, 68, 0.15);
          color: var(--danger, #ef4444);
          font-size: 14px;
        }

        .client-code {
          font-family: monospace;
          font-weight: 600;
          letter-spacing: 1px;
        }

        .client-name {
          font-weight: 500;
        }

        .inactive-row td {
          opacity: 0.55;
        }

        .status-badge {
          padding: 4px 10px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 500;
        }

        .status-badge.status-active {
          background: rgba(34, 197, 94, 0.2);
          color: var(--success, #22c55e);
        }

        .status-badge.status-inactive {
          background: rgba(100, 116, 139, 0.2);
          color: var(--secondary, #64748b);
        }

        .action-buttons {
          display: flex;
          gap: 4px;
        }

        .empty-row {
          text-align: center;
          padding: 32px 16px;
          color: var(--text-secondary);
        }

        .loading {
          text-align: center;
          padding: 40px;
          color: var(--text-secondary);
        }

        .form-error {
          padding: 10px 14px;
          margin-bottom: 16px;
          border-radius: 8px;
          background: rgba(239, 68, 68, 0.15);
          color: var(--danger, #ef4444);
          font-size: 14px;
        }
      `}</style>
    </div>
  );
};

export default ClientsManager;
